/* eslint-disable */
import parameters from './parameters';

const MM_TO_PX = 3;
let DOTS_PER_MM = 8;

const pxToDots = (px) => Math.round((Number(px) || 0) * (DOTS_PER_MM / MM_TO_PX));
const mmToDots = (mm) => Math.round((Number(mm) || 0) * DOTS_PER_MM);

// Map a CSS px corner radius to ZPL's ^GB rounding index (0-8, 8 = fully rounded).
// ZPL radius ≈ (rounding/8) * (min(w,h)/2), so rounding ≈ radiusDots / (minSide/2) * 8.
// Size-relative (not a flat px clamp) so a given radius rounds the same proportion on any
// box and round-trips with the decoder (roundingToRadiusPx in zplToComponents.js).
const roundingFor = (radiusPx, wDots, hDots) => {
	const r = Number(radiusPx) || 0;
	const minHalf = Math.min(wDots || 1, hDots || 1) / 2;
	if (r <= 0 || minHalf <= 0) return 0;
	return Math.min(8, Math.max(1, Math.round((pxToDots(r) / minHalf) * 8)));
};

const isBoldNode = (node, inherited) => {
	const tag = node.tagName;
	if (tag === 'B' || tag === 'STRONG') return true;
	const fw = node.style?.fontWeight;
	if (fw === 'bold' || fw === 'bolder' || Number(fw) >= 600) return true;
	if (fw === 'normal' || (Number(fw) > 0 && Number(fw) < 600)) return false;
	return inherited;
};

const isHighlightNode = (node, inherited) => {
	const bg = node.style?.backgroundColor;
	if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') return inherited;
	if (bg === 'rgb(255, 255, 255)' || bg === '#ffffff' || bg === 'white') return false;
	return true;
};

const htmlToRuns = (html) => {
	if (!html) return [];
	if (typeof document === 'undefined') {
		return [{ text: String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, ''), bold: false, hl: false }];
	}
	const div = document.createElement('div');
	div.innerHTML = String(html);
	const runs = [];
	const walk = (node, bold, hl) => {
		if (node.nodeType === 3) {
			runs.push({ text: node.textContent || '', bold, hl });
			return;
		}
		if (node.nodeType !== 1) return;
		const tag = node.tagName;
		if (tag === 'BR') { runs.push({ text: '\n', bold, hl }); return; }
		const b = isBoldNode(node, bold);
		const h = isHighlightNode(node, hl);
		node.childNodes.forEach((c) => walk(c, b, h));
		if (tag === 'DIV' || tag === 'P' || tag === 'TR') runs.push({ text: '\n', bold: false, hl: false });
	};
	div.childNodes.forEach((c) => walk(c, false, false));
	return runs;
};

const htmlToFlat = (html) => {
	const chars = [];
	for (const r of htmlToRuns(html)) {
		const t = String(r.text || '').replace(/\u00a0/g, ' ');
		for (const c of t) chars.push({ c, bold: !!r.bold, hl: !!r.hl });
	}

	const lines = [];
	let cur = [];
	for (const ch of chars) {
		if (ch.c === '\n') { lines.push(cur); cur = []; } else cur.push(ch);
	}
	lines.push(cur);
	const normLines = lines.map((line) => {
		const out = [];
		let prevSpace = false;
		for (const ch of line) {
			const ws = ch.c === ' ' || ch.c === '\t';
			if (ws) {
				if (prevSpace) continue;
				out.push({ c: ' ', bold: ch.bold, hl: ch.hl });
				prevSpace = true;
			} else {
				out.push(ch);
				prevSpace = false;
			}
		}
		while (out.length && out[0].c === ' ') out.shift();
		while (out.length && out[out.length - 1].c === ' ') out.pop();
		return out;
	}).filter((l) => l.length > 0); // blank lines drop
	// re-join with single newline separators
	const flat = [];
	normLines.forEach((line, i) => {
		if (i) flat.push({ c: '\n', bold: false, hl: false });
		line.forEach((ch) => flat.push(ch));
	});
	return flat;
};

const htmlToText = (html) => htmlToFlat(html).map((x) => x.c).join('');

const hasInlineBold = (html) => htmlToFlat(html).some((x) => x.bold && x.c !== ' ' && x.c !== '\n');

const hasInlineHighlight = (html) => htmlToFlat(html).some((x) => x.hl && x.c !== '\n');
const measureCtx = (() => {
	if (typeof document === 'undefined') return null;
	try { return document.createElement('canvas').getContext('2d'); } catch (e) { return null; }
})();


const measureWidth = (text, bold, fh) => {
	if (!text) return 0;
	if (!measureCtx) return text.length * fh * (bold ? 0.62 : 0.58);
	measureCtx.font = `${bold ? 'bold ' : ''}${fh}px Arial, Helvetica, sans-serif`;
	return measureCtx.measureText(text).width;
};


const groupRuns = (chars) => {
	const runs = [];
	for (const ch of chars) {
		const last = runs[runs.length - 1];
		if (last && last.bold === ch.bold && last.hl === ch.hl) last.text += ch.c;
		else runs.push({ text: ch.c, bold: ch.bold, hl: ch.hl });
	}
	return runs;
};

const widthOfChars = (chars, fh) => groupRuns(chars).reduce((a, r) => a + measureWidth(r.text, r.bold, fh), 0);

// greedy word-wrap that keeps each char's bold flag and the x offset of every run
const layoutRichLines = (flat, tw, fh) => {
	const paras = [];
	let para = [];
	for (const ch of flat) {
		if (ch.c === '\n') { paras.push(para); para = []; } else para.push(ch);
	}
	paras.push(para);

	const lines = []; // each line = [{ x, text, bold }]
	for (const p of paras) {
		// tokenize into whitespace / word tokens
		const tokens = [];
		let buf = null;
		for (const ch of p) {
			const type = (ch.c === ' ' || ch.c === '\t') ? 'ws' : 'word';
			if (!buf || buf.type !== type) { buf = { type, chars: [] }; tokens.push(buf); }
			buf.chars.push(ch);
		}

		let segs = [];
		let lineW = 0;       // current ink width on the line
		let pendingWs = 0;   // space width waiting between words
		const flush = () => { lines.push(segs); segs = []; lineW = 0; pendingWs = 0; };

		for (const tk of tokens) {
			if (tk.type === 'ws') {
				if (lineW > 0) pendingWs += widthOfChars(tk.chars, fh); // drop leading spaces
				continue;
			}
			const wordW = widthOfChars(tk.chars, fh);
			if (lineW > 0 && lineW + pendingWs + wordW > tw) flush();
			let runX = lineW > 0 ? lineW + pendingWs : 0;
			for (const r of groupRuns(tk.chars)) {
				const rw = measureWidth(r.text, r.bold, fh);
				segs.push({ x: Math.round(runX), w: Math.round(rw), text: r.text, bold: r.bold, hl: r.hl });
				runX += rw;
			}
			lineW = runX;
			pendingWs = 0;
		}
		flush();
	}
	return lines;
};

// Render rich text (per-word bold + highlight) as ZPL: wrap manually, draw a black
// box behind highlighted runs (reverse-printed white), fake-bold the bold runs.
// originX/originY = top-left of the text area (padding already applied by caller).
const richRunsZpl = ({ content, fnt, fh, lineSpace, originX, originY, tw, maxLines, forceBold, align }) => {
	const lines = layoutRichLines(htmlToFlat(content), tw, fh).slice(0, maxLines);
	const step = fh + lineSpace;
	// per-line horizontal offset for center/right alignment (justify -> left).
	const lineOffset = (segs) => {
		if (align !== 'C' && align !== 'R') return 0;
		const lineW = segs.length ? segs[segs.length - 1].x + segs[segs.length - 1].w : 0;
		const free = Math.max(0, tw - lineW);
		return align === 'C' ? Math.round(free / 2) : free;
	};
	let out = '';
	lines.forEach((segs, i) => {
		const ly = originY + i * step;
		const off = lineOffset(segs);
		// highlight: merge consecutive highlighted segments into one black box
		// (covers the spaces between highlighted words too).
		for (let j = 0; j < segs.length;) {
			if (!segs[j].hl) { j++; continue; }
			let k = j;
			while (k + 1 < segs.length && segs[k + 1].hl) k++;
			const x0 = segs[j].x;
			const x1 = segs[k].x + segs[k].w;
			out += `^FO${originX + off + x0 - 2},${ly - 2}^GB${(x1 - x0) + 4},${fh + 4},${fh + 4},B^FS\n`;
			j = k + 1;
		}
		// text: highlighted runs reverse-print (white on the box); others normal.
		for (const s of segs) {
			const fd = toFieldData(s.text);
			if (!fd) continue;
			const fx = originX + off + s.x;
			const fr = s.hl ? '^FR' : '';
			const bold = s.bold || forceBold;
			const pass = (ox, oy) => `^FO${fx + ox},${ly + oy}^A${fnt}N,${fh},${fh}${fr}^FD${fd}^FS\n`;
			out += pass(0, 0);
			// double-print skipped on highlight (two ^FR XOR cancel the glyph)
			if (bold && !s.hl) out += pass(1, 0) + pass(0, 1);
		}
	});
	return out;
};

const wantsBold = (comp) => (!!comp?.isBold || hasInlineBold(comp?.content))

const sanitize = (text) => String(text || '')
	.replace(/\^/g, ' ')
	.replace(/~/g, ' ')
	.replace(/\\/g, ' ');

const toFieldData = (text) => sanitize(text).replace(/\r?\n/g, '\\&');

const resolveTextData = (comp) => (
	comp?.placeholder
		? `{${comp.placeholder}}`
		: toFieldData(htmlToText(comp?.content))
);

const orientAngle = (rotate) => (((Math.round((Number(rotate) || 0) / 90) * 90) % 360) + 360) % 360;
const imageToGfa = (img, wDots, hDots, angle = 0) => {
	const rotated = angle === 90 || angle === 270;
	const cw = rotated ? hDots : wDots;
	const ch = rotated ? wDots : hDots;
	const canvas = document.createElement('canvas');
	canvas.width = cw;
	canvas.height = ch;
	const ctx = canvas.getContext('2d');
	ctx.fillStyle = '#ffffff';
	ctx.fillRect(0, 0, cw, ch);
	ctx.translate(cw / 2, ch / 2);
	ctx.rotate((angle * Math.PI) / 180);
	const iw = img.naturalWidth || img.width || wDots;
	const ih = img.naturalHeight || img.height || hDots;
	const scale = Math.min(wDots / iw, hDots / ih);
	const dw = Math.max(1, Math.round(iw * scale));
	const dh = Math.max(1, Math.round(ih * scale));
	ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
	ctx.setTransform(1, 0, 0, 1, 0, 0);
	const { data } = ctx.getImageData(0, 0, cw, ch);

	// Fixed threshold dono cases me fail karta hai: chhota (128) -> light/colored logos
	// (white bg wale) gayab; bada (200) -> photos/mid-tone images puri kali. Isliye Otsu's
	// method se HAR image ka apna optimal threshold nikaalo (ink vs background ko brightness
	// histogram se auto-separate karta hai). Transparent pixels white maano.
	const lumAt = (idx) => 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
	const hist = new Array(256).fill(0);
	let opaque = 0;
	for (let i = 0; i < data.length; i += 4) {
		if (data[i + 3] < 128) continue; // transparent -> background
		hist[Math.min(255, Math.round(lumAt(i)))] += 1;
		opaque += 1;
	}
	// Otsu: between-class variance maximize karne wala threshold
	let sumAll = 0;
	for (let i = 0; i < 256; i++) sumAll += i * hist[i];
	let wB = 0;
	let sumB = 0;
	let maxVar = -1;
	let threshold = 128;
	for (let t = 0; t < 256; t++) {
		wB += hist[t];
		if (wB === 0) continue;
		const wF = opaque - wB;
		if (wF === 0) break;
		sumB += t * hist[t];
		const mB = sumB / wB;
		const mF = (sumAll - sumB) / wF;
		const between = wB * wF * (mB - mF) * (mB - mF);
		if (between > maxVar) { maxVar = between; threshold = t; }
	}

	const bytesPerRow = Math.ceil(cw / 8);
	const total = bytesPerRow * ch;
	let hex = '';
	for (let y = 0; y < ch; y++) {
		for (let bx = 0; bx < bytesPerRow; bx++) {
			let byte = 0;
			for (let bit = 0; bit < 8; bit++) {
				const px = bx * 8 + bit;
				let dark = 0;
				if (px < cw) {
					const idx = (y * cw + px) * 4;
					// ink = transparent nahi AND Otsu threshold se darker
					dark = (data[idx + 3] >= 128 && lumAt(idx) <= threshold) ? 1 : 0;
				}
				byte = (byte << 1) | dark;
			}
			hex += byte.toString(16).padStart(2, '0').toUpperCase();
		}
	}
	return `^GFA,${total},${total},${bytesPerRow},${hex}`;
};

const imageToZpl = (comp, x, y, w, h) => new Promise((resolve) => {
	if (!comp?.src) {
		resolve('');
		return;
	}
	const img = new Image();
	img.crossOrigin = 'anonymous';
	img.onload = () => {
		try {
			const angle = orientAngle(comp?.rotate);
			const rotated = angle === 90 || angle === 270;
			const gfa = imageToGfa(img, Math.max(1, w), Math.max(1, h), angle);
			const fx = rotated ? x + Math.round((w - h) / 2) : x;
			const fy = rotated ? y + Math.round((h - w) / 2) : y;
			resolve(`^FO${fx},${fy}${gfa}^FS\n`);
		} catch (e) {
			resolve('');
		}
	};
	img.onerror = () => resolve('');
	img.src = comp.src;
});


const ratiosToDots = (n, ratios, total) => {
	const count = Math.max(1, n || 1);
	let r = Array.isArray(ratios) && ratios.length === count
		? ratios.map((v) => Number(v) || 0)
		: Array.from({ length: count }, () => 1 / count);
	const sum = r.reduce((a, b) => a + b, 0) || 1;
	r = r.map((v) => v / sum);
	const sizes = [];
	let used = 0;
	for (let i = 0; i < count; i++) {
		if (i === count - 1) {
			sizes.push(total - used);
		} else {
			const s = Math.round(total * r[i]);
			sizes.push(s);
			used += s;
		}
	}
	return sizes;
};

const tableToZpl = (comp, x, y, w, h) => {
	const rows = Math.max(1, comp?.rows || 1);
	const cols = Math.max(1, comp?.cols || 1);
	const cells = comp?.cells || [];
	const colW = ratiosToDots(cols, comp?.colWidths, w);
	const rowHBase = ratiosToDots(rows, comp?.rowHeights, h);
	const rowH = rowHBase.map((hh, r) => {
		let maxFont = 0;
		for (let c = 0; c < cols; c++) {
			const f = pxToDots(cells?.[r]?.[c]?.fontSize) || 16;
			if (f > maxFont) maxFont = f;
		}
		return Math.max(hh, maxFont + 12);
	});
	const colX = [];
	const rowY = [];
	for (let c = 0, acc = 0; c < cols; c++) { colX[c] = acc; acc += colW[c]; }
	for (let r = 0, acc = 0; r < rows; r++) { rowY[r] = acc; acc += rowH[r]; }
	const totalH = rowH.reduce((a, b) => a + b, 0);
	const t = Math.max(2, comp?.thickness || 1);
	let zpl = '';
	zpl += `^FO${x},${y}^GB${w},${totalH},${t}^FS\n`;
	for (let r = 0; r < rows; r++) {
		for (let c = 0; c < cols; c++) {
			const cellW = colW[c];
			const cellH = rowH[r];
			const cx = x + colX[c];
			const cy = y + rowY[r];
			zpl += `^FO${cx},${cy}^GB${cellW},${cellH},${t}^FS\n`;
			const cell = cells?.[r]?.[c];
			if (cell?.isHighlight) zpl += `^FO${cx},${cy}^GB${cellW},${cellH},${cellH},B^FS\n`;
			zpl += borderZpl(cell, cx, cy, cellW, cellH);
			const data = toFieldData(htmlToText(cell?.content));
			if (data) {
				const fh = pxToDots(cell?.fontSize) || 16;
				const maxLines = Math.max(1, Math.floor((cellH - 8) / fh));
				// text ko cell me thoda vertical-center karo (single line ke liye)
				const ty = cy + Math.max(4, Math.round((cellH - fh) / 2));
				const co = orientChar(cell?.rotate); // per-cell rotation
				const cellAngle = orientAngle(cell?.rotate);
				const cInlineBold = hasInlineBold(cell?.content);
				const cInlineHl = hasInlineHighlight(cell?.content);
				// inline bold/highlight: same per-word rich layout as staticText.
				// only for un-rotated, non-whole-highlighted cells (else ^FB path).
				if (cellAngle === 0 && !cell?.isHighlight && measureCtx
					&& (cInlineHl || (cInlineBold && !cell?.isBold))) {
					zpl += richRunsZpl({
						content: cell?.content,
						fnt: zplFont(cell?.fontFamily),
						fh,
						lineSpace: 0,
						originX: cx + 6,
						originY: ty,
						tw: cellW - 12,
						maxLines,
						forceBold: !!cell?.isBold,
						align: zplAlign(cell?.textAlign),
					});
				} else {
					const fr = cell?.isHighlight ? '^FR' : '';
					const cellField = (ox, oy, fd) => `^FO${cx + 6 + ox},${ty + oy}^A${zplFont(cell?.fontFamily)}${co},${fh},${fh}^FB${cellW - 12},${maxLines},0,${zplAlign(cell?.textAlign)},0${fr}^FD${fd}^FS\n`;
					zpl += cellField(0, 0, data);
					// bold -> extra passes at x+1 aur y+1 (heavier/darker). highlight pe skip
					// (dono ^FR XOR ho ke glyph cancel kar dete).
					if (wantsBold(cell) && !cell?.isHighlight) zpl += cellField(1, 0, data) + cellField(0, 1, data);
				}
			}
		}
	}
	return zpl;
};

const borderZpl = (comp, x, y, w, h) => {
	if (!comp?.borderStyle || comp.borderStyle === 'none') return '';
	const t = Math.max(2, comp?.thickness || 1);
	const a = orientAngle(comp?.rotate);
	let bx = x, by = y, bw = w, bh = h;
	if (a === 90 || a === 270) {
		bw = h;
		bh = w;
		bx = x + Math.round((w - h) / 2);
		by = y + Math.round((h - w) / 2);
	}
	const rounding = roundingFor(comp?.borderRadius, bw, bh);
	return `^FO${bx},${by}^GB${bw || 1},${bh || 1},${t},B,${rounding}^FS\n`;
};

const highlightZpl = (comp, x, y, w, h) => {
	if (!comp?.isHighlight) return '';
	const a = orientAngle(comp?.rotate);
	let bx = x, by = y, bw = w, bh = h;
	if (a === 90 || a === 270) {
		bw = h;
		bh = w;
		bx = x + Math.round((w - h) / 2);
		by = y + Math.round((h - w) / 2);
	}
	const W = bw || 1;
	const H = bh || 1;
	return `^FO${bx},${by}^GB${W},${H},${H},B^FS\n`;
};

const orientChar = (rotate) => {
	const a = orientAngle(rotate);
	return a === 90 ? 'R' : a === 180 ? 'I' : a === 270 ? 'B' : 'N';
};

const fieldOrigin = (comp, x, y, w, h) => {
	const a = orientAngle(comp?.rotate);
	if (a === 90 || a === 270) {
		return {
			fx: x + Math.round((w - h) / 2),
			fy: y + Math.round((h - w) / 2),
		};
	}
	return { fx: x, fy: y };
};

const zplFont = (f) => {
	const v = String(f || '0').toUpperCase();
	return /^[0-9A-Z]$/.test(v) ? v : '0';
};

// CSS text-align -> ZPL ^FB justification code (L/C/R/J).
const zplAlign = (a) => {
	switch (String(a || 'left').toLowerCase()) {
		case 'center': return 'C';
		case 'right': return 'R';
		case 'justify': return 'J';
		default: return 'L';
	}
};

const componentToZpl = async (comp) => {
	const x = pxToDots(comp?.position?.x);
	const y = pxToDots(comp?.position?.y);
	const w = pxToDots(comp?.size?.width);
	const h = pxToDots(comp?.size?.height);
	const o = orientChar(comp?.rotate);

	switch (comp?.name) {
		case 'text': {
			const fh = pxToDots(comp?.fontSize) || 20;
			const lh = Number(comp?.lineHeight) || 1.2;
			const padX = Math.round(fh * 0.22);
			const padY = Math.round(fh * 0.35);
			const tw = Math.max(1, (w || 1) - padX * 2);
			const maxLines = Math.max(1, Math.floor((h - padY) / fh));
			const lineSpace = Math.max(0, Math.round((lh - 1) * fh));
			const data = resolveTextData(comp);
			const fnt = zplFont(comp?.fontFamily);
			const fr = comp?.isHighlight ? '^FR' : '';
			const field = (ox, oy, fd) => `^FO${x + padX + ox},${y + padY + oy}^A${fnt}${o},${fh},${fh}^FB${tw},${maxLines},${lineSpace},${zplAlign(comp?.textAlign)},0${fr}^FD${fd}^FS\n`;
			let out = highlightZpl(comp, x, y, w, h) + borderZpl(comp, x, y, w, h) + field(0, 0, data);
			if (comp?.isBold && !comp?.isHighlight) out += field(1, 0, data) + field(0, 1, data);
			return out;
		}
		case 'staticText': {
			const fh = pxToDots(comp?.fontSize) || 18;
			const lh = Number(comp?.lineHeight) || 1.2;
			const padX = Math.round(fh * 0.22);
			const padY = Math.round(fh * 0.35);
			const tw = Math.max(1, (w || 1) - padX * 2);
			const maxLines = Math.max(1, Math.floor((h - padY) / (fh * lh)));
			const fnt = zplFont(comp?.fontFamily);
			const lineSpace = Math.max(0, Math.round((lh - 1) * fh));
			const angle = orientAngle(comp?.rotate);
			const inlineBold = hasInlineBold(comp?.content);
			const inlineHl = hasInlineHighlight(comp?.content);
			if (angle === 0 && !comp?.isHighlight && measureCtx
				&& (inlineHl || (inlineBold && !comp?.isBold))) {
				return borderZpl(comp, x, y, w, h) + richRunsZpl({
					content: comp?.content,
					fnt,
					fh,
					lineSpace,
					originX: x + padX,
					originY: y + padY,
					tw,
					maxLines,
					forceBold: !!comp?.isBold,
					align: zplAlign(comp?.textAlign),
				});
			}

			const data = toFieldData(htmlToText(comp?.content));
			const fr = comp?.isHighlight ? '^FR' : '';
			return highlightZpl(comp, x, y, w, h)
				+ borderZpl(comp, x, y, w, h)
				+ `^FO${x + padX},${y + padY}^A${fnt}${o},${fh},${fh}^FB${tw},${maxLines},${lineSpace},${zplAlign(comp?.textAlign)},0${fr}^FD${data}^FS\n`;
		}
		case 'line': {
			const t = Math.max(2, pxToDots(comp?.thickness || 2));
			const a = orientAngle(comp?.rotate);
			if (a === 90 || a === 270) {
				const cx = x + Math.round(w / 2);
				const sy = y + Math.round(h / 2) - Math.round((w || 1) / 2);
				return `^FO${cx},${sy}^GB${t},${w || 1},${t}^FS\n`;
			}
			const cy = y + Math.round(h / 2);
			return `^FO${x},${cy}^GB${w || 1},${t},${t}^FS\n`;
		}
		case 'qrCode': {
			const qrData = String(parameters?.qrCode ?? '');
			const len = qrData.length;
			const modules = len <= 17 ? 21 : len <= 32 ? 25 : len <= 53 ? 29 : len <= 78 ? 33 : 37;
			const a = orientAngle(comp?.rotate);
			const bw = (a === 90 || a === 270) ? h : w; // rotated bounding box dims
			const bh = (a === 90 || a === 270) ? w : h;
			const boxDots = Math.min(bw || 1, bh || 1);
			const mag = Math.max(1, Math.floor(boxDots / (modules + 8)));
			const { fx, fy } = fieldOrigin(comp, x, y, w, h);
			return borderZpl(comp, x, y, w, h)
				+ `^FO${fx},${fy}^BQ${o},2,${mag}^FDLA,{qrCode}^FS\n`;
		}
		case 'barcode': {
			const data = String(parameters?.barCode ?? '');
			const estModules = Math.max(1, data.length * 11 + 35);
			const mod = Math.max(1, Math.floor((w || 1) / estModules));
			const { fx, fy } = fieldOrigin(comp, x, y, w, h);
			return borderZpl(comp, x, y, w, h)
				+ `^FO${fx},${fy}^BY${mod}^BC${o},${h || 50},N,N,N^FD{barCode}^FS\n`;
		}
		case 'table':
			return tableToZpl(comp, x, y, w, h);
		case 'image': {
			const img = await imageToZpl(comp, x, y, w, h);
			return borderZpl(comp, x, y, w, h) + img;
		}
		case 'rectangle': {
			// ^GBw,h,t[,B,rounding]; rounded corners via the 5th param (0-8). A filled
			// rectangle uses thickness = shorter side. Rotation swaps the bounding box.
			const t = Math.max(1, pxToDots(comp?.thickness || 1));
			const a = orientAngle(comp?.rotate);
			let bx = x, by = y, bw = w, bh = h;
			if (a === 90 || a === 270) {
				bw = h; bh = w;
				bx = x + Math.round((w - h) / 2);
				by = y + Math.round((h - w) / 2);
			}
			if (comp?.fill) {
				return `^FO${bx},${by}^GB${bw || 1},${bh || 1},${Math.min(bw || 1, bh || 1)},B^FS\n`;
			}
			const rounding = roundingFor(comp?.borderRadius, bw, bh);
			return `^FO${bx},${by}^GB${bw || 1},${bh || 1},${t},B,${rounding}^FS\n`;
		}
		case 'circle': {
			// ^GCd,t,c — diameter, border thickness, colour. Filled = thickness >= radius.
			const d = Math.max(3, Math.min(w || 1, h || 1));
			const t = Math.max(1, pxToDots(comp?.thickness || 1));
			const th = comp?.fill ? Math.ceil(d / 2) : t;
			return `^FO${x},${y}^GC${d},${th},B^FS\n`;
		}
		default:
			return '';
	}
};

export const generateZpl = async (labelDetails, components = []) => {
	DOTS_PER_MM = Number(labelDetails?.dpmm) || 8;
	const pw = mmToDots(labelDetails?.size?.width);
	const ll = mmToDots(labelDetails?.size?.height);
	let zpl = '^XA\n^CI28\n^LH0,0\n^LS0\n'; // CI28 = UTF-8
	if (pw) zpl += `^PW${pw}\n`;
	if (ll) zpl += `^LL${ll}\n`;
	if (labelDetails?.borderStyle && labelDetails.borderStyle !== 'none' && pw && ll) {
		const t = Math.max(1, pxToDots(labelDetails?.thickness || 1));
		zpl += `^FO0,0^GB${pw},${ll},${t}^FS\n`;
	}
	for (const comp of (components || [])) {
		// eslint-disable-next-line no-await-in-loop
		zpl += await componentToZpl(comp);
	}
	zpl += '^XZ';
	return zpl;
};

export default generateZpl;
